import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import {
  WorkflowManagerConfigRulesQuery,
  WorkflowManagerConfigType,
} from "generated-graphql/graphql";
import { GridRowHeightParams } from "@mui/x-data-grid-premium";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";
import { groupBy } from "lodash";
import { prettyPrintDateMed } from "util/dates";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { TruncateTypography } from "components/TruncateTypography";

const WORKFLOW_MANAGER_CONFIG_RULES_QUERY = gql(`
  query WorkflowManagerConfigRules($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    workflowManagerConfigRules(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        type
        value
        humanReadableValue
        notes
        createdAt
        createdBy
        updatedAt
        updatedBy
        facility {
          id
          name
        }
        tenant {
          id
          name
        }
      }
    }
  }
`);

export type WorkflowConfigRuleRow =
  WorkflowManagerConfigRulesQuery["workflowManagerConfigRules"]["items"][number];

type SectionHeaderRow = {
  __isSectionHeader: true;
  __groupName: string;
  id: string;
};

type GroupedRow = WorkflowConfigRuleRow | SectionHeaderRow;

function isWorkflowConfigRuleRow(
  row: GroupedRow
): row is WorkflowConfigRuleRow {
  return !("__isSectionHeader" in row && row.__isSectionHeader);
}

const getTypeEmojiAndTooltip = (
  type: WorkflowManagerConfigType,
  value: string
): { emoji: string; tooltip: string } => {
  // For ALLOW_STATE and ALLOW_STATE_REVISION, check value patterns
  if (
    type === WorkflowManagerConfigType.AllowState ||
    type === WorkflowManagerConfigType.AllowStateRevision
  ) {
    if (value.endsWith("_NEEDS_CANDIDATE")) {
      return {
        emoji: "🟠",
        tooltip: "Blocked - needs test candidate",
      };
    }
    if (value.endsWith("_NOT_IMPLEMENTED")) {
      return {
        emoji: "🔴",
        tooltip: "Blocked - not implemented",
      };
    }
    // Regular 2-letter state code
    return {
      emoji: "🟢",
      tooltip: "Allowed",
    };
  }

  // For other rule types
  switch (type) {
    case WorkflowManagerConfigType.DenyFacility:
    case WorkflowManagerConfigType.DenyTenant:
    case WorkflowManagerConfigType.DenyFacilityRevision:
    case WorkflowManagerConfigType.DenyTenantRevision:
    case WorkflowManagerConfigType.DenyLobMailToSerc:
      return {
        emoji: "⛔",
        tooltip: "Explicitly denied",
      };
    case WorkflowManagerConfigType.AllowFacility:
    case WorkflowManagerConfigType.AllowFacilityRevision:
      return {
        emoji: "🟢",
        tooltip: "Allowed",
      };
    case WorkflowManagerConfigType.StateBypassValidation:
    case WorkflowManagerConfigType.FacilityBypassValidation:
      return {
        emoji: "🔑",
        tooltip: "Allowed but skips validation",
      };
    case WorkflowManagerConfigType.MaxConcurrency:
    case WorkflowManagerConfigType.MinConductExecutionTime:
    case WorkflowManagerConfigType.ForceReportTypeForSubmission:
    case WorkflowManagerConfigType.TestingFailureRate:
    case WorkflowManagerConfigType.WebAutomationStateOverrideUseAa:
    case WorkflowManagerConfigType.Version:
    case WorkflowManagerConfigType.SchedulerWorkIntervalSleepTimeMs:
    case WorkflowManagerConfigType.AllowEnvironment:
      return {
        emoji: "⚙️",
        tooltip: "System configuration",
      };
    default:
      return {
        emoji: "⚠️",
        tooltip: "Unknown Format (WARNING)",
      };
  }
};

const getGroupForType = (
  type: WorkflowManagerConfigType
):
  | "States"
  | "State Revisions"
  | "Facilities"
  | "Facility Revisions"
  | "Tenants"
  | "Other Config" => {
  switch (type) {
    case WorkflowManagerConfigType.AllowState:
      return "States";
    case WorkflowManagerConfigType.AllowStateRevision:
      return "State Revisions";
    case WorkflowManagerConfigType.AllowFacility:
    case WorkflowManagerConfigType.DenyFacility:
      return "Facilities";
    case WorkflowManagerConfigType.AllowFacilityRevision:
    case WorkflowManagerConfigType.DenyFacilityRevision:
      return "Facility Revisions";
    case WorkflowManagerConfigType.DenyTenant:
    case WorkflowManagerConfigType.DenyTenantRevision:
      return "Tenants";
    default:
      return "Other Config";
  }
};

const groupRowsByType = (items: WorkflowConfigRuleRow[]): GroupedRow[] => {
  // Group items by their type category
  const grouped = groupBy(items, (item) => getGroupForType(item.type));

  // Define the order of groups
  const groupOrder: Array<
    | "States"
    | "State Revisions"
    | "Facilities"
    | "Facility Revisions"
    | "Tenants"
    | "Other Config"
  > = [
    "States",
    "State Revisions",
    "Facilities",
    "Facility Revisions",
    "Tenants",
    "Other Config",
  ];

  const result: GroupedRow[] = [];

  groupOrder.forEach((groupName) => {
    const groupItems = grouped[groupName] || [];
    if (groupItems.length > 0) {
      // Sort items within the group by type, then by humanReadableValue
      const sortedGroupItems = [...groupItems].sort((a, b) => {
        // First sort by type
        const typeComparison = a.type.localeCompare(b.type);
        if (typeComparison !== 0) {
          return typeComparison;
        }
        // Then sort by humanReadableValue
        return (a.humanReadableValue || "").localeCompare(
          b.humanReadableValue || ""
        );
      });

      // Add section header
      result.push({
        __isSectionHeader: true,
        __groupName: groupName,
        id: `__section-header-${groupName}`,
      });
      // Add sorted items in this group
      result.push(...sortedGroupItems);
    }
  });

  return result;
};

export function Rules() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Rules",
    },
  ]);

  const columns = useMemo(
    (): OmnisearchGridColDef<GroupedRow>[] => [
      {
        field: "type",
        headerName: "Type",
        width: isMobile ? 120 : 200,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(WorkflowManagerConfigType),
        enumPresentationFunction: (enumValue: string) => {
          const type = enumValue as WorkflowManagerConfigType;
          // For enum presentation, we don't have access to value, so use default emoji
          const { emoji } = getTypeEmojiAndTooltip(type, "");
          const prettyValue = prettyPrintEnumValue(type);
          return emoji ? `${emoji} ${prettyValue}` : prettyValue;
        },
        renderCell: ({ row }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return (
              <Typography
                variant="subtitle2"
                className="section-header-row-title"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                {row.__groupName}
              </Typography>
            );
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return null;
          }
          const { emoji, tooltip } = getTypeEmojiAndTooltip(
            row.type,
            row.value
          );
          const prettyValue = prettyPrintEnumValue(row.type);
          return (
            <Typography
              variant="body2"
              sx={{ py: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {emoji ? (
                <Tooltip title={tooltip} arrow>
                  <span style={{ cursor: "help" }}>{emoji}</span>
                </Tooltip>
              ) : null}
              {prettyValue}
            </Typography>
          );
        },
      },
      {
        field: "humanReadableValue",
        headerName: "Value",
        flex: isMobile ? 2 : 4,
        minWidth: 200,
        sortable: true,
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null; // Section header name shown in Type column
          }
          return (
            <Typography variant="body2" sx={{ py: 0.5 }}>
              {value}
            </Typography>
          );
        },
      },
      {
        field: "rawType",
        headerName: "Raw Type",
        width: 200,
        sortable: true,
        filterable: false,
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return row.type;
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return (
            <Typography
              variant="body2"
              sx={{ py: 0.5, fontFamily: "monospace" }}
            >
              {value}
            </Typography>
          );
        },
      },
      {
        field: "rawValue",
        headerName: "Raw Value",
        width: 200,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return row.value;
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return (
            <Typography
              variant="body2"
              sx={{ py: 0.5, fontFamily: "monospace" }}
            >
              {value}
            </Typography>
          );
        },
      },

      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 150,
        sortable: true,
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value ? (
            <Stack>
              <TruncateTypography
                variant="body2"
                sx={{
                  py: 0.5,
                }}
                showTooltip={true}
              >
                {value}
              </TruncateTypography>
            </Stack>
          ) : null;
        },
      },
      {
        field: "updatedBy",
        headerName: "Updated By",
        width: 120,
        sortable: true,
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value;
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        width: 120,
        sortable: true,
        filterKeyType: "date",
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return prettyPrintDateMed(row.updatedAt);
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value;
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        width: 120,
        sortable: true,
        filterKeyType: "date",
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return prettyPrintDateMed(row.createdAt);
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value;
        },
      },
    ],
    [isMobile]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="workflow-config-rules-table"
        isURLDriven
        columns={columns}
        dataQuery={WORKFLOW_MANAGER_CONFIG_RULES_QUERY}
        excludeFilterColumns={[]}
        additionalFilterColumns={[
          {
            key: "facility",
            header: "Facility",
            filterKeyType: "multipleFacility",
          },
          {
            key: "tenant",
            header: "Organization",
            filterKeyType: "multipleTenant",
          },
        ]}
        initialSortModel={[
          { field: "type", sort: "asc" },
          { field: "humanReadableValue", sort: "asc" },
        ]}
        getItems={(data) => {
          const items = data.workflowManagerConfigRules.items;
          return groupRowsByType(items);
        }}
        getCount={(data) => data.workflowManagerConfigRules.count}
        getRowId={(row) => row.id}
        isRowSelectable={(params) => {
          const row = params.row;
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return false;
          }
          return true;
        }}
        noDataMessage="No workflow configuration rules found."
        initialPageSize={25}
        getRowHeight={(params: GridRowHeightParams) => {
          if (typeof params.id === "string" && params.id.startsWith("__section-header-")) {
            return 48; // Taller for section headers
          }
          return "auto";
        }}
        gridSx={{
          // Style section header rows - target by class name in first cell using :has() selector
          "& .MuiDataGrid-row:has(.section-header-row-title)": {
            backgroundColor: theme.palette.grey[200],
            borderBottom: `2px solid ${theme.palette.divider}`,
            "&:hover": {
              backgroundColor: theme.palette.grey[200],
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              paddingTop: 1.5,
              paddingBottom: 1.5,
            },
            "& .MuiDataGrid-cell:first-of-type": {
              paddingLeft: 2,
            },
          },
        }}
        columnVisibilityModel={{
          // Hide raw columns by default for all screen sizes
          rawType: false,
          rawValue: false,
          createdAt: false,
        }}
      />
    </Box>
  );
}
